/**
 * ConfigErrorScreen
 *
 * The visible-error-with-retry state for a genuinely failed GET /config —
 * network failure or schema drift. Shared by OnboardingRoute and
 * OnboardedRoute (App.tsx) so both route guards render the SAME honest
 * failure instead of each hand-rolling their own, and instead of the old
 * behaviour: `configApi.getConfig()` used to swallow every failure and
 * return a hardcoded FALLBACK_CONFIG, indistinguishable from a real backend
 * response — the app then ran silently on stale English-only defaults with
 * `org.self_assign` absent. There is no fallback config anymore: a failed
 * fetch is always this screen, never a fabricated substitute.
 */

import { useTranslation } from 'react-i18next';
import { getTraceId } from '@/utils/logger';

export function ConfigErrorScreen({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center h-screen bg-neutral-50">
      <div className="text-center p-6">
        <p className="text-neutral-600 mb-4">{t('errors.configLoadFailed')}</p>
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600"
        >
          {t('actions.retry')}
        </button>
        <p className="text-xs text-neutral-400 mt-4">{t('errors.backendUnreachable')}</p>
        <p className="text-[10px] text-neutral-300 mt-2 font-mono">Debug: {getTraceId()}</p>
      </div>
    </div>
  );
}
